import React, { useEffect, useMemo, useRef, useState } from "react";
import { AssignmentReturn, ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Card,
  useMediaQuery,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import Slider from "react-slick";
import { useDispatch, useSelector } from "react-redux";
import TextFieldController from "../../../components/inputs/TextFieldController";
import DropdownController from "../../../components/inputs/DropdownController";
import DatePickerController from "../../../components/inputs/DatePickerController";
import CheckboxController from "../../../components/inputs/CheckboxController";
import RadioController from "../../../components/inputs/RadioController";
import FreeTextDropdownController from "../../../components/inputs/FreeTextDropdownController";
import { entityNamesForCustomId, serviceNames } from "../../../utils/constants";
import { getService } from "../../../reducers/service";
import { calculateAge } from "../../../utils/functions";
import { formatToBase64ImageUrl } from "../../../utils/functions";
import CityDialog from "../../../components/CityDialog";
import SectionTitle from "../../../components/SectionTitle";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import { PerPhotoType } from "../../../utils/enums";
import { notify } from "../../../reducers/NotifierReducer";

const renderFormInput = ({
  field,
  control,
  errors,
  watch,
  options,
  highlight,
  dateHighlight,
  handleAddClick,
  editPermission,
}) => {
  const shouldHighlight = field.name === "status" && !highlight;

  return (
    <>
      {field.component === "TextField" && (
        <TextFieldController
          name={field.name}
          label={field.label}
          type={field.type}
          required={field.required}
          control={control}
          errors={errors}
          isPhoneNumber={field.isPhoneNumber}
          isSSN={field.isSSN}
          disabled={
            !editPermission ||
            (typeof field.isDisabled === "function"
              ? field.isDisabled(watch())
              : field.isDisabled)
          }
        />
      )}
      {field.component === "Dropdown" && (
        <DropdownController
          name={field.name}
          label={field.label}
          options={options[field.name] || []}
          control={control}
          errors={errors}
          highlight={shouldHighlight}
          startIcon={field.startIcon}
          disabled={
            !editPermission ||
            (typeof field.isDisabled === "function"
              ? field.isDisabled(watch())
              : field.isDisabled)
          }
          handleIconClick={handleAddClick}
        />
      )}
      {field.component === "DatePicker" && (
        <DatePickerController
          name={field.name}
          label={field.label}
          control={control}
          errors={errors}
          disableFuture={field.disableFuture}
          highlight={dateHighlight}
          disabled={
            !editPermission ||
            (typeof field.isDisabled === "function"
              ? field.isDisabled(watch())
              : field.isDisabled)
          }
        />
      )}
      {field.component === "Checkbox" && (
        <CheckboxController
          name={field.name}
          label={field.label}
          required={field.required}
          control={control}
          errors={errors}
          disabled={
            !editPermission ||
            (typeof field.isDisabled === "function"
              ? field.isDisabled(watch())
              : field.isDisabled)
          }
        />
      )}
      {field.component === "Radio" && (
        <RadioController
          name={field.name}
          label={field.label}
          options={options[field.name] || []}
          required={field.required}
          control={control}
          errors={errors}
          disabled={
            !editPermission ||
            (typeof field.isDisabled === "function"
              ? field.isDisabled(watch())
              : field.isDisabled)
          }
        />
      )}
      {field.component === "FreeTextDropdown" && (
        <FreeTextDropdownController
          name={field.name}
          label={field.label}
          options={options[field.name] || []}
          control={control}
          errors={errors}
          disabled={
            !editPermission ||
            (typeof field.isDisabled === "function"
              ? field.isDisabled(watch())
              : field.isDisabled)
          }
        />
      )}
    </>
  );
};

const GeneralInfoSection = ({
  title,
  icon,
  control,
  errors,
  options,
  generalInfoFields,
  theme,
  allExpanded = false,
  defaultExpanded = false,
  watch,
  setValue,
  formValues,
  id,
  statusHighlight,
  dateHighlight,
  isLoadingPhotos,
  editPermission,
}) => {
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const isInitialMount = useRef(true);
  const userAgency = useSelector((state) => state.user.userAgency);
  const [expanded, setExpanded] = useState(defaultExpanded || allExpanded);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [openCityDialog, setOpenCityDialog] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const dictionary = useSelector((state) => state.dictionary.dictionary);
  const cityOptions = useMemo(() => {
    return (dictionary.codeCities || []).map((g) => ({
      label: g.State ? `${g.CityDescription}, ${g.State}` : g.CityDescription,
      value: g.CityDescription,
      state: g.State,
    }));
  }, [dictionary.codeCities]);

  const rCity = watch("rCity");
  const mCity = watch("mCity");

  useEffect(() => {
    if (rCity) {
      const matchedCity = cityOptions.find((city) => city.value === rCity);
      if (matchedCity?.state) {
        setValue("rState", matchedCity.state);
      }
    }

    if (mCity) {
      const matchedCity = cityOptions.find((city) => city.value === mCity);
      if (matchedCity?.state) {
        setValue("mState", matchedCity.state);
      }
    }
  }, [mCity, rCity, cityOptions, setValue]);

  const photos = useSelector((state) =>
    (
      state.personnelPhotos.photosByEntity?.[PerPhotoType.Employee]?.[id] || []
    ).sort((a, b) => {
      if (a.isThumbnail && !b.isThumbnail) return -1;
      if (!a.isThumbnail && b.isThumbnail) return 1;

      return new Date(b.createdAt) - new Date(a.createdAt);
    })
  );

  const hasPhotos = photos && photos.length > 0;

  const sliderSettings = {
    dots: true,
    infinite: photos.length > 1,
    speed: 500,
    arrows: true,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  // Sync with allExpanded, but skip on initial mount to respect defaultExpanded
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setExpanded(allExpanded);
  }, [allExpanded]);

  const generateCustomId = async () => {
    const data = {
      EntityName: entityNamesForCustomId.personnelEmployeeId,
      agencyID: userAgency,
    };
    const service = getService(serviceNames.customId);
    service.timeout = 20000;

    const id = await service.create(data);
    if (id?.includes("Agency ID does not match")) {
      dispatch(
        notify(
          "Unable to generate Employee ID because the Agency ID does not match.",
          "warning"
        )
      );
      return null;
    }
    return id;
  };

  const autoGenerateID = watch("autoGenerateID");

  useEffect(() => {
    const fetchAndSetId = async () => {
      if (autoGenerateID) {
        const customId = await generateCustomId();
        if (customId) {
          setValue("employeeNum", customId);
        }
      }
    };
    fetchAndSetId();
  }, [autoGenerateID, setValue]);

  const confirmCopyAddress = () => {
    const currentMailingValues = watch(["mAddress", "mCity", "mState", "mZip"]);

    const hasExistingMailingData = Object.values(currentMailingValues).some(
      (value) => value
    );

    if (hasExistingMailingData) {
      setShowConfirmDialog(true);
      return;
    }

    copyAddress();
  };

  const copyAddress = () => {
    const values = formValues;
    setValue("mAddress", values.address || "");
    setValue("mCity", values.rCity || "");
    setValue("mState", values.rState || "");
    setValue("mZip", values.rZip || "");
  };

  const employeeNum = watch("employeeNum");

  const handleAutoGenerateClick = () => {
    if (employeeNum) {
      setOpenDialog(true);
    }
  };

  const generateEmployeeId = async () => {
    setOpenDialog(false);
    const customId = await generateCustomId();
    if (customId) {
      setValue("employeeNum", customId);
    }
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded((prev) => !prev)}
        sx={{ mb: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: theme.accordion.backgroundColor,
            color: theme.accordion.textColor,
          }}
        >
          <Box display="flex" alignItems="center">
            {icon && <Box sx={{ mr: 1 }}>{icon}</Box>}
            {title}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ marginTop: theme.spacing(1) }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={9}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={2}>
                  {renderFormInput({
                    field: generalInfoFields.employeeNum,
                    control,
                    errors,
                    watch,
                    options,
                    editPermission,
                  })}
                </Grid>
                <Grid item xs={12} md={0.7}>
                  <Box
                    sx={
                      isSmall
                        ? { display: "flex", justifyContent: "center" }
                        : {}
                    }
                  >
                    {editPermission && (
                      <Tooltip title="Auto generate employee number">
                        <IconButton
                          color="primary"
                          aria-label="Auto generate employee number"
                          size="large"
                          onClick={handleAutoGenerateClick}
                        >
                          <AssignmentReturn />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={1.8}>
                  {renderFormInput({
                    field: generalInfoFields.status,
                    control,
                    errors,
                    watch,
                    options,
                    highlight: statusHighlight,
                    editPermission,
                  })}
                </Grid>
                <Grid item xs={12} md={1.5}>
                  {renderFormInput({
                    field: generalInfoFields.suffix,
                    control,
                    errors,
                    watch,
                    options,
                    editPermission,
                  })}
                </Grid>
                <Grid item xs={12} md={2}>
                  {renderFormInput({
                    field: generalInfoFields.lastName,
                    control,
                    errors,
                    watch,
                    options,
                    editPermission,
                  })}
                </Grid>
                <Grid item xs={12} md={2}>
                  {renderFormInput({
                    field: generalInfoFields.firstName,
                    control,
                    errors,
                    watch,
                    options,
                    editPermission,
                  })}
                </Grid>
                <Grid item xs={12} md={2}>
                  {renderFormInput({
                    field: generalInfoFields.middleName,
                    control,
                    errors,
                    watch,
                    options,
                    editPermission,
                  })}
                </Grid>
                <Grid item xs={12} md={4}>
                  {renderFormInput({
                    field: generalInfoFields.race,
                    control,
                    errors,
                    watch,
                    options,
                    editPermission,
                  })}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {renderFormInput({
                    field: generalInfoFields.gender,
                    control,
                    errors,
                    watch,
                    options,
                    editPermission,
                  })}
                </Grid>
                <Grid item xs={12} sm={3}>
                  {renderFormInput({
                    field: generalInfoFields.dob,
                    control,
                    errors,
                    watch,
                    options,
                    editPermission,
                  })}
                </Grid>
                <Grid item xs={12} sm={1}>
                  <Box mt={2}>
                    Age: {watch("dob") ? calculateAge(watch("dob")) : "-"}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            {
              <Grid
                item
                xs={12}
                md={3}
                justifyContent="center"
                alignItems="center"
              >
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={11}
                  display="flex"
                  justifyContent="center"
                >
                  <Card
                    style={{
                      position: "relative",
                      padding: 8,
                      textAlign: "center",
                      width: "250px",
                      maxWidth: 600,
                      height: "auto",
                      marginLeft: isSmall ? 20 : 60,
                    }}
                  >
                    <Box position="relative">
                      {isLoadingPhotos ? (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          height={150}
                        >
                          <CircularProgress size={34} sx={{ mr: 1, ml: 1 }} />
                        </Box>
                      ) : hasPhotos ? (
                        <Slider {...sliderSettings}>
                          {photos.map((photo, index) => (
                            <div key={index}>
                              <img
                                alt={`personnel-${index}`}
                                src={formatToBase64ImageUrl(photo.imageData)}
                                style={{
                                  objectFit: "contain",
                                  Width: "280px",
                                  height: "120px",
                                  margin: "auto",
                                  display: "block",
                                }}
                              />
                            </div>
                          ))}
                        </Slider>
                      ) : (
                        <Box
                          sx={{
                            Width: "280px",
                            height: "120px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          No Preview
                        </Box>
                      )}
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            }
            <Grid item xs={12} md={9}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={5.5}>
                  <SectionTitle title="Residential Address">
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        {renderFormInput({
                          field: generalInfoFields.address,
                          control,
                          errors,
                          watch,
                          options,
                          editPermission,
                        })}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {renderFormInput({
                          field: generalInfoFields.rCity,
                          control,
                          errors,
                          watch,
                          options: { rCity: cityOptions },
                          handleAddClick: () => setOpenCityDialog(true),
                          editPermission,
                        })}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {renderFormInput({
                          field: generalInfoFields.rState,
                          control,
                          errors,
                          watch,
                          options,
                          editPermission,
                        })}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {renderFormInput({
                          field: generalInfoFields.rZip,
                          control,
                          errors,
                          watch,
                          options,
                          editPermission,
                        })}
                      </Grid>
                    </Grid>
                  </SectionTitle>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={1}
                  display="flex"
                  alignItems={{ xs: "start", md: "center" }}
                  justifyContent="center"
                >
                  <Box sx={{ mt: { xs: 2, md: 0 } }}>
                    {editPermission && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={confirmCopyAddress}
                      >
                        Copy &gt;&gt;
                      </Button>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={5.5}>
                  <SectionTitle title="Mailing Address">
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        {renderFormInput({
                          field: generalInfoFields.mAddress,
                          control,
                          errors,
                          watch,
                          options,
                          editPermission,
                        })}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {renderFormInput({
                          field: generalInfoFields.mCity,
                          control,
                          errors,
                          watch,
                          options: { mCity: cityOptions },
                          handleAddClick: () => setOpenCityDialog(true),
                          editPermission,
                        })}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {renderFormInput({
                          field: generalInfoFields.mState,
                          control,
                          errors,
                          watch,
                          options,
                          editPermission,
                        })}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {renderFormInput({
                          field: generalInfoFields.mZip,
                          control,
                          errors,
                          watch,
                          options,
                          editPermission,
                        })}
                      </Grid>
                    </Grid>
                  </SectionTitle>
                </Grid>
              </Grid>

              <Grid item xs={12} mt={1}>
                <SectionTitle title="Additional Information">
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                      {renderFormInput({
                        field: generalInfoFields.dl,
                        control,
                        errors,
                        watch,
                        options,
                        editPermission,
                      })}
                    </Grid>
                    <Grid item xs={12} md={3}>
                      {renderFormInput({
                        field: generalInfoFields.dlState,
                        control,
                        errors,
                        watch,
                        options,
                        editPermission,
                      })}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {renderFormInput({
                        field: generalInfoFields.dlExpire,
                        control,
                        errors,
                        watch,
                        options,
                        dateHighlight: dateHighlight,
                        editPermission,
                      })}
                    </Grid>
                    <Grid item xs={12} md={2.4}>
                      {renderFormInput({
                        field: generalInfoFields.maritalStatus,
                        control,
                        errors,
                        watch,
                        options,
                        editPermission,
                      })}
                    </Grid>
                    <Grid item xs={12} md={2.4}>
                      {renderFormInput({
                        field: generalInfoFields.spouseName,
                        control,
                        errors,
                        watch,
                        options,
                        editPermission,
                      })}
                    </Grid>
                    <Grid item xs={12} md={2.4}>
                      {renderFormInput({
                        field: generalInfoFields.spouseSSN,
                        control,
                        errors,
                        watch,
                        options,
                        editPermission,
                      })}
                    </Grid>
                    <Grid item xs={12} md={2.4}>
                      {renderFormInput({
                        field: generalInfoFields.spouseEmployer,
                        control,
                        errors,
                        watch,
                        options,
                        editPermission,
                      })}
                    </Grid>
                    <Grid item xs={12} md={2.4}>
                      {renderFormInput({
                        field: generalInfoFields.spousePhoneNumber,
                        control,
                        errors,
                        watch,
                        options,
                        editPermission,
                      })}
                    </Grid>
                  </Grid>
                </SectionTitle>
              </Grid>
            </Grid>
            <Grid item xs={12} md={3}>
              <SectionTitle title="Contact Methods">
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {renderFormInput({
                      field: generalInfoFields.phoneNo,
                      control,
                      errors,
                      watch,
                      options,
                      editPermission,
                    })}
                  </Grid>
                  <Grid item xs={12}>
                    {renderFormInput({
                      field: generalInfoFields.cellNo,
                      control,
                      errors,
                      watch,
                      options,
                      editPermission,
                    })}
                  </Grid>
                  <Grid item xs={12}>
                    {renderFormInput({
                      field: generalInfoFields.pagerNo,
                      control,
                      errors,
                      watch,
                      options,
                      editPermission,
                    })}
                  </Grid>
                  <Grid item xs={12}>
                    {renderFormInput({
                      field: generalInfoFields.workPhoneNum,
                      control,
                      errors,
                      watch,
                      options,
                      editPermission,
                    })}
                  </Grid>
                  <Grid item xs={12}>
                    {renderFormInput({
                      field: generalInfoFields.email,
                      control,
                      errors,
                      watch,
                      options,
                      editPermission,
                    })}
                  </Grid>
                </Grid>
              </SectionTitle>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={2}>
                  {renderFormInput({
                    field: generalInfoFields.hireDate,
                    control,
                    errors,
                    watch,
                    options,
                    editPermission,
                  })}
                </Grid>
                <Grid item xs={12} md={2}>
                  {renderFormInput({
                    field: generalInfoFields.latestHireDate,
                    control,
                    errors,
                    watch,
                    options,
                    editPermission,
                  })}
                </Grid>
                <Grid item xs={12} md={2}>
                  {renderFormInput({
                    field: generalInfoFields.commissionedDate,
                    control,
                    errors,
                    watch,
                    options,
                    editPermission,
                  })}
                </Grid>
                <Grid item xs={12} md={2}>
                  {renderFormInput({
                    field: generalInfoFields.residentialCounty,
                    control,
                    errors,
                    watch,
                    options,
                    editPermission,
                  })}
                </Grid>
                <Grid item xs={12} md={2}>
                  {renderFormInput({
                    field: generalInfoFields.assignedCounty,
                    control,
                    errors,
                    watch,
                    options,
                    editPermission,
                  })}
                </Grid>
                <Grid item xs={12} md={2}>
                  {renderFormInput({
                    field: generalInfoFields.badgeNum,
                    control,
                    errors,
                    watch,
                    options,
                    editPermission,
                  })}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
      >
        <DialogTitle>Overwrite Mailing Address?</DialogTitle>
        <DialogContent>
          <Typography>
            Mailing address fields already have values. Do you want to replace
            it?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowConfirmDialog(false);
            }}
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              copyAddress();
              setShowConfirmDialog(false);
            }}
            color="primary"
            variant="contained"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <CityDialog
        open={openCityDialog}
        onClose={() => {
          setOpenCityDialog(false);
        }}
      />
      <ConfirmationDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onConfirm={() => generateEmployeeId()}
        title="Confirmation"
        content="Are you sure you want to replace the existing employee number?"
      />
    </>
  );
};

export default GeneralInfoSection;
